import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';
import { EventType, InteractionType } from '@azure/msal-browser';
import {
  MsalAuthenticationTemplate,
  useIsAuthenticated,
} from '@azure/msal-react';
import propTypes from 'prop-types';
import { loginRequest } from 'core/auth/authConfig';
import { msalInstance } from 'index';
import NotFound from 'views/NotFoundPage';
import {
  activeProjectChanged,
  selectActiveProjectKey,
} from 'core/slices/projects';
import { AppDispatch } from 'core/store';
import { useGetProjectsQuery } from 'core/services/ApiSlice';
import MainLayout from '../layouts/MainLayout';
import { GridReports } from 'features/gridReport/GridReports';
import { PbiReports } from 'features/pbiReport/PbiReports';
import { LoadingComponent } from './LoadingComponent';
import { ErrorComponent } from './ErrorComponent';

const App: React.FC = () => {
  const activeProjectKey = useSelector(selectActiveProjectKey);
  const dispatch = useDispatch<AppDispatch>();

  msalInstance.addEventCallback(event => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as any;
      const account = payload.account;
      msalInstance.setActiveAccount(account);
    }
  });

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  const ProjectsProvider = ({
    children,
  }: {
    children: propTypes.ReactNodeLike;
  }) => {
    const auth = useIsAuthenticated();
    const {
      data: projects,
      isFetching,
      isError,
    } = useGetProjectsQuery(undefined, { skip: !auth });

    // Choose first project automatically if none selected
    useEffect(() => {
      if (projects && projects.length > 0 && !activeProjectKey) {
        dispatch(activeProjectChanged(projects[0].key));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects, activeProjectKey, dispatch]);

    if (isFetching) return <LoadingComponent />;
    if (!auth) {
      return (
        <ErrorComponent msg="You are not authenticated. Please sign in to access the application." />
      );
    }
    if (isError || !projects || projects.length === 0 || !activeProjectKey) {
      return <ErrorComponent />;
    }

    return <>{children}</>;
  };

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={loginRequest}
      errorComponent={({ error }) => (
        <ErrorComponent
          msg={error?.message || 'An authentication error occurred.'}
        />
      )}
      loadingComponent={LoadingComponent}
    >
      <ProjectsProvider>
        <Routes>
          <Route path="" element={<Navigate to={'/reports'} />} />
          <Route path="/reports" element={<MainLayout />}>
            <Route
              path="/reports/pbi/:reportId"
              element={<PbiReports activeProjectKey={activeProjectKey} />}
            />
            <Route
              path="/reports/grid/:reportId"
              element={<GridReports activeProjectKey={activeProjectKey} />}
            />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ProjectsProvider>
    </MsalAuthenticationTemplate>
  );
};

export default App;
